(function () {
  // Every `.oddit-cart-drawer` root on the page is driven from here: the overlay
  // drawer in the header group AND the /cart page's `--page` variant, which is
  // the same markup rendered inline. Binding only the first match left the cart
  // page's quantity and remove buttons dead.
  var roots = [];
  var drawer = null;

  function resolveRoots() {
    roots = Array.prototype.slice.call(document.querySelectorAll('.oddit-cart-drawer'));
    roots.forEach(function (root) {
      // Liquid ships with display:none to prevent FOUC
      root.style.display = '';
    });
    var overlays = roots.filter(function (root) { return !root.hasAttribute('data-main-cart'); });
    drawer = overlays[0] || roots[0] || null;
  }
  resolveRoots();
  if (!drawer) return;

  // Query across every root, handing the handler the root it came from.
  function eachIn(selector, fn) {
    roots.forEach(function (root) {
      Array.prototype.forEach.call(root.querySelectorAll(selector), function (el) {
        fn(el, root);
      });
    });
  }

  var controller = new AbortController();
  var signal = controller.signal;

  var overlay, closeBtn, products, checkoutBtn;

  // Re-runnable: a section reload replaces the drawer's DOM.
  function resolveRefs() {
    overlay = drawer.querySelector('.oddit-cart-drawer__overlay');
    closeBtn = drawer.querySelector('.oddit-cart-drawer__close');
    products = drawer.querySelector('.oddit-cart-drawer__products');
    checkoutBtn = drawer.querySelector('[data-cart-checkout], .oddit-cart-drawer__checkout-cta, .oddit-cart-drawer__checkout');
  }
  resolveRefs();

  var threshold = parseFloat(drawer.getAttribute('data-free-shipping-threshold')) || 0;
  var activeCurrency = null;
  var lastFocused = null;

  // -----------------------------------------------------------------
  // Focus trap
  // -----------------------------------------------------------------
  function getFocusable() {
    return Array.prototype.slice.call(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var f = getFocusable();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // -----------------------------------------------------------------
  // Open / close
  // -----------------------------------------------------------------
  function openDrawer() {
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    // Refresh cart on open so state is always canonical
    refreshCart().catch(function () {});
    setTimeout(function () {
      var f = getFocusable();
      if (f.length) f[0].focus();
    }, 50);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  drawer.querySelectorAll('[data-cart-drawer-close], .oddit-cart-drawer__close, .oddit-cart-drawer__overlay').forEach(function (el) {
    el.addEventListener('click', closeDrawer, { signal: signal });
  });

  document.addEventListener('keydown', function (e) {
    if (!drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeDrawer();
    else trapFocus(e);
  }, { signal: signal });

  // -----------------------------------------------------------------
  // Money formatting — currency resolved dynamically, never hardcoded
  // -----------------------------------------------------------------
  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // -----------------------------------------------------------------
  // Cart API
  // -----------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('getCart failed');
      return r.json();
    });
  }

  function addToCart(variantId, quantity, properties, sellingPlanId) {
    quantity = quantity == null ? 1 : quantity;
    properties = properties || {};
    var payload = { id: variantId, quantity: quantity, properties: properties };
    if (sellingPlanId) payload.selling_plan = sellingPlanId;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function () {
      return getCart();
    }).then(function (cart) {
      renderCart(cart);
      openDrawer();
      return cart;
    });
  }

  function addMultipleToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function () {
      return getCart();
    }).then(function (cart) {
      renderCart(cart);
      openDrawer();
      return cart;
    });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  // `quantity` is sent explicitly: changing the selling plan re-keys the line, and
  // without it the rebuilt line comes back as a single unit — switching 3 one-time
  // units to a subscription left only 1 in the cart.
  function updateLineItemToSubscription(lineItemKey, sellingPlanId, quantity) {
    var payload = { id: lineItemKey, selling_plan: sellingPlanId };
    if (quantity != null && quantity > 0) payload.quantity = quantity;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  // -----------------------------------------------------------------
  // Product cache
  //
  // /cart.js gives the APPLIED selling plan but not the available ones, and no
  // compare-at price. /products/<handle>.js has both, so line items that need
  // either are resolved from here. Cached per handle for the page's lifetime.
  // -----------------------------------------------------------------
  var productCache = new Map();

  function fetchProduct(handle) {
    if (productCache.has(handle)) return Promise.resolve(productCache.get(handle));
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (product) {
        productCache.set(handle, product);
        return product;
      });
  }

  // Warm the cache for everything in the cart, so the render below stays sync.
  function ensureProducts(cart) {
    var handles = [];
    (cart.items || []).forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });
    return Promise.all(handles.map(fetchProduct)).then(function () { return cart; });
  }

  function variantOf(item) {
    var product = productCache.get(item.handle);
    if (!product) return null;
    return (product.variants || []).filter(function (v) {
      return v.id === item.variant_id;
    })[0] || null;
  }

  // The plan a non-subscription line would switch to. Read off the VARIANT's own
  // allocations, never the product's plan groups: a product can carry a plan that
  // only some variants are sold on, and offering it on a variant without one both
  // showed an upsell that shouldn't be there and failed when taken.
  function firstSellingPlanId(item) {
    var variant = variantOf(item);
    if (!variant) return null;
    var allocation = (variant.selling_plan_allocations || [])[0];
    if (!allocation) return null;
    return allocation.selling_plan_id || (allocation.selling_plan && allocation.selling_plan.id) || null;
  }

  // Mirrors snippets/oddit-cart-item-price.liquid — keep the two in step.
  // Prefers the real line discount, then the selling plan's pre-discount price,
  // then the variant's compare-at.
  function priceParts(item) {
    var final = item.final_line_price;
    var compare = item.original_line_price;
    if (compare <= final) {
      var planCompare = 0;
      var allocation = item.selling_plan_allocation;
      if (allocation && allocation.compare_at_price > allocation.price) {
        planCompare = allocation.compare_at_price * item.quantity;
      }
      var variantCompare = 0;
      var variant = variantOf(item);
      if (variant && variant.compare_at_price > variant.price) {
        variantCompare = variant.compare_at_price * item.quantity;
      }
      compare = Math.max(planCompare, variantCompare);
    }
    var savePercent = compare > final
      ? Math.round((compare - final) * 100 / compare)
      : 0;
    return { final: final, compare: compare, savePercent: savePercent };
  }

  // Recharge quantity upsells set "Product Quantity" above 1. Monthly plans send 0.
  function isQuantityUpsell(item) {
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    var options = (plan && plan.options) || [];
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      if (opt && opt.name === 'Product Quantity' && parseInt(opt.value, 10) > 1) return true;
    }
    return false;
  }

  // -----------------------------------------------------------------
  // Line item template
  // -----------------------------------------------------------------
  function lineItemHTML(item, root) {
    root = root || drawer;
    var img = item.image ? item.image.replace(/(\.[^./]+)(\?.*)?$/, '_200x$1$2') : '';
    var variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? '<p class="oddit-cart-drawer__item-subtitle">' + escapeHtml(item.variant_title) + '</p>'
      : '';
    var url = item.url || '#';
    var rawTitle = item.product_title || item.title || '';
    var title = escapeHtml(rawTitle); // full title — used for aria-labels / alt
    // Visible title mirrors the Liquid snippet: trimmed at the first " - " when the theme setting is on.
    var trimTitle = root.getAttribute('data-trim-title') === 'true';
    var displayTitle = escapeHtml(trimTitle ? rawTitle.split(' - ')[0].trim() : rawTitle);

    var planName = item.selling_plan_allocation
      && item.selling_plan_allocation.selling_plan
      && item.selling_plan_allocation.selling_plan.name;
    var planHTML = planName
      ? '<p class="oddit-cart-drawer__item-plan">' + escapeHtml(planName) + '</p>'
      : '';

    var price = priceParts(item);
    var saveHTML = price.savePercent > 0
      ? '<s class="oddit-cart-drawer__item-compare">' + formatMoney(price.compare) + '</s>' +
        '<span class="oddit-cart-drawer__item-save">SAVE ' + price.savePercent + '%</span>'
      : '';

    // Per-line subscription switch — mirrors the Liquid template. Only for
    // one-time lines whose product has a selling plan (known once cached).
    var switchHTML = '';
    if (!item.selling_plan_allocation) {
      var switchPlanId = firstSellingPlanId(item);
      var switchLabel = root.getAttribute('data-subscribe-label') || '';
      if (switchPlanId && switchLabel) {
        switchHTML =
          '<button class="oddit-cart-drawer__switch-cta" type="button" data-line-switch' +
            ' data-line-item-key="' + escapeHtml(item.key) + '"' +
            ' data-selling-plan-id="' + switchPlanId + '"' +
            ' aria-label="Switch ' + title + ' to subscription">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M13.6941 5.5H13.7141C13.8467 5.5 13.9738 5.55268 14.0676 5.64645C14.1614 5.74021 14.2141 5.86739 14.2141 6C14.2141 6.13261 14.1614 6.25979 14.0676 6.35355C13.9738 6.44732 13.8467 6.5 13.7141 6.5C12.4271 6.5 11.4321 6.5 10.6371 6.564C9.84106 6.627 9.30105 6.751 8.90306 6.964C8.12622 7.3858 7.50227 8.04194 7.12006 8.839C6.91006 9.272 6.79206 9.795 6.73206 10.566C6.67206 11.343 6.67206 12.328 6.67206 13.68V14.393L8.21806 12.846C8.31194 12.7522 8.43923 12.6996 8.57191 12.6997C8.70459 12.6998 8.8318 12.7526 8.92556 12.8465C9.01931 12.9404 9.07193 13.0677 9.07183 13.2004C9.07174 13.333 9.01894 13.4602 8.92506 13.554L6.52506 15.954C6.47708 16.0018 6.4199 16.0394 6.35697 16.0644C6.29405 16.0895 6.22669 16.1015 6.15898 16.0997C6.09128 16.0979 6.02464 16.0824 5.96312 16.0541C5.90159 16.0258 5.84645 15.9853 5.80106 15.935L3.62906 13.535C3.5438 13.4361 3.50063 13.3077 3.50879 13.1774C3.51695 13.047 3.57579 12.925 3.67272 12.8375C3.76966 12.75 3.89699 12.7038 4.02749 12.709C4.158 12.7141 4.28131 12.7701 4.37106 12.865L5.67106 14.302V13.658C5.67106 12.333 5.67106 11.308 5.73506 10.488C5.80006 9.659 5.93205 8.996 6.22006 8.403C6.69469 7.41603 7.46881 6.60411 8.43206 6.083C9.01206 5.772 9.71505 5.634 10.5581 5.567C11.3981 5.5 12.4311 5.5 13.6941 5.5ZM17.8411 7.9C17.9088 7.90181 17.9755 7.91737 18.037 7.94573C18.0985 7.9741 18.1537 8.01467 18.1991 8.065L20.3711 10.465C20.4563 10.5639 20.4995 10.6923 20.4913 10.8226C20.4832 10.953 20.4243 11.075 20.3274 11.1625C20.2305 11.25 20.1031 11.2962 19.9726 11.291C19.8421 11.2859 19.7188 11.2299 19.6291 11.135L18.3291 9.698V10.342C18.3291 11.668 18.3291 12.692 18.2651 13.512C18.2001 14.341 18.0681 15.004 17.7801 15.597C17.3054 16.584 16.5313 17.3959 15.5681 17.917C14.9881 18.228 14.2851 18.366 13.4421 18.433C12.6031 18.5 11.5691 18.5 10.3061 18.5H10.2861C10.1534 18.5 10.0263 18.4473 9.9325 18.3536C9.83873 18.2598 9.78606 18.1326 9.78606 18C9.78606 17.8674 9.83873 17.7402 9.9325 17.6464C10.0263 17.5527 10.1534 17.5 10.2861 17.5C11.5731 17.5 12.5681 17.5 13.3631 17.436C14.1591 17.373 14.6991 17.249 15.0971 17.036C15.8743 16.6144 16.4986 15.9582 16.8811 15.161C17.0911 14.727 17.2081 14.205 17.2681 13.434C17.3281 12.657 17.3281 11.672 17.3281 10.32V9.607L15.7821 11.154C15.6882 11.2478 15.5609 11.3004 15.4282 11.3003C15.2955 11.3002 15.1683 11.2474 15.0746 11.1535C14.9808 11.0596 14.9282 10.9323 14.9283 10.7996C14.9284 10.667 14.9812 10.5398 15.0751 10.446L17.4751 8.046C17.523 7.99814 17.5802 7.96052 17.6431 7.93542C17.706 7.91033 17.7733 7.89828 17.8411 7.9Z" fill="#434167"/></svg>' +
            '<span>' + escapeHtml(switchLabel) + '</span>' +
          '</button>';
      }
    }

    return '' +
      '<article class="oddit-cart-drawer__item" data-line-item data-line-item-key="' + escapeHtml(item.key) + '">' +
        '<a href="' + escapeHtml(url) + '" class="oddit-cart-drawer__item-image-wrap" aria-label="' + title + '">' +
          (img ? '<img class="oddit-cart-drawer__item-image" src="' + escapeHtml(img) + '" alt="' + title + '" loading="lazy" width="100" height="100">' : '') +
        '</a>' +
        '<div class="oddit-cart-drawer__item-details">' +
          '<div class="oddit-cart-drawer__item-titles">' +
            '<h3 class="oddit-cart-drawer__item-title"><a href="' + escapeHtml(url) + '">' + displayTitle + '</a></h3>' +
            variantTitle +
          '</div>' +
          planHTML +
          '<div class="oddit-cart-drawer__item-price-row">' +
            '<p class="oddit-cart-drawer__item-price">' + formatMoney(price.final) + '</p>' +
            saveHTML +
          '</div>' +
          '<div class="oddit-cart-drawer__item-actions">' +
            (function () {
              var locked = isQuantityUpsell(item);
              var decrease = locked ? '' :
                '<button class="oddit-cart-drawer__quantity-btn" type="button" data-quantity-action="decrease" data-quantity-decrease data-line-key="' + escapeHtml(item.key) + '" data-line-item-key="' + escapeHtml(item.key) + '" aria-label="Decrease quantity of ' + title + '">' +
                  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M2 6.99989C2 6.66656 2.22222 6.44434 2.55556 6.44434H11.4444C11.5918 6.44434 11.7331 6.50287 11.8373 6.60705C11.9415 6.71124 12 6.85255 12 6.99989C12 7.14723 11.9415 7.28854 11.8373 7.39273C11.7331 7.49692 11.5918 7.55545 11.4444 7.55545H2.55556C2.40821 7.55545 2.26691 7.49692 2.16272 7.39273C2.05853 7.28854 2 7.14723 2 6.99989Z" fill="#353535"/></svg>' +
                '</button>';
              var increase = locked ? '' :
                '<button class="oddit-cart-drawer__quantity-btn" type="button" data-quantity-action="increase" data-quantity-increase data-line-key="' + escapeHtml(item.key) + '" data-line-item-key="' + escapeHtml(item.key) + '" aria-label="Increase quantity of ' + title + '">' +
                  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.55762 6.45616C2.41044 6.45616 2.26929 6.51463 2.16522 6.6187C2.06115 6.72277 2.00269 6.86392 2.00269 7.0111C2.00269 7.15828 2.06115 7.29943 2.16522 7.4035C2.26929 7.50757 2.41044 7.56603 2.55762 7.56603H6.44217L6.45327 11.4506C6.454 11.5235 6.46908 11.5955 6.49764 11.6625C6.5262 11.7296 6.56769 11.7903 6.61973 11.8414C6.67178 11.8924 6.73336 11.9326 6.80097 11.9599C6.86858 11.9871 6.94088 12.0007 7.01376 12C7.08663 11.9992 7.15865 11.9842 7.2257 11.9556C7.29275 11.927 7.35351 11.8856 7.40453 11.8335C7.45554 11.7815 7.49581 11.7199 7.52302 11.6523C7.55024 11.5847 7.56387 11.5124 7.56314 11.4395V7.55494L11.4477 7.54384C11.5206 7.54311 11.5926 7.52803 11.6596 7.49947C11.7267 7.47091 11.7875 7.42942 11.8385 7.37738C11.8895 7.32533 11.9297 7.26375 11.957 7.19614C11.9842 7.12853 11.9978 7.05623 11.9971 6.98335C11.9964 6.91048 11.9813 6.83846 11.9527 6.77141C11.9242 6.70436 11.8827 6.64359 11.8306 6.59258C11.7786 6.54156 11.717 6.5013 11.6494 6.47408C11.5818 6.44687 11.5095 6.43324 11.4366 6.43397H7.55204L7.54095 2.54941C7.53947 2.40224 7.4796 2.26167 7.37448 2.15864C7.26937 2.05561 7.12764 1.99856 6.98046 2.00003C6.83328 2.0015 6.69272 2.06138 6.58969 2.16649C6.48666 2.2716 6.4296 2.41333 6.43107 2.56051V6.44506L2.54652 6.45616H2.55762Z" fill="#353535"/></svg>' +
                '</button>';
              return '<div class="oddit-cart-drawer__quantity' + (locked ? ' oddit-cart-drawer__quantity--locked' : '') + '">' +
                decrease +
                '<span class="oddit-cart-drawer__quantity-value" data-quantity-value data-line-item-key="' + escapeHtml(item.key) + '">' + item.quantity + '</span>' +
                increase +
              '</div>';
            })() +
            '<button class="oddit-cart-drawer__remove" type="button" data-remove-line data-remove-item data-line-key="' + escapeHtml(item.key) + '" data-line-item-key="' + escapeHtml(item.key) + '" aria-label="Remove ' + title + '">' +
              '<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10.7205 1.88235L10.2499 0H2.92729L2.45671 1.88235H0V2.82353H0.470588V16H12.7059L12.7068 2.82353H13.1774V1.88235H10.7205ZM3.66165 0.941176H9.51553L9.75082 1.88235H3.42635L3.66165 0.941176ZM11.7654 15.0588H1.41247V2.82353H11.7654V15.0588Z" fill="#353535"/><path d="M4.23633 6.58813H5.1775V11.294H4.23633V6.58813Z" fill="#353535"/><path d="M8.00098 6.58813H8.94215V11.294H8.00098V6.58813Z" fill="#353535"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
        switchHTML +
      '</article>';
  }

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  // Paint now with whatever is cached, then repaint once if a product still
  // needs fetching — a cold cache costs a missing save badge, not a delay.
  function renderCart(cart) {
    if (!cart) return;
    paintCart(cart);
    var missing = (cart.items || []).some(function (item) {
      return item.handle && !productCache.has(item.handle);
    });
    if (missing) {
      ensureProducts(cart).then(function () { paintCart(cart); });
    }
  }

  function paintCart(cart) {
    if (!cart) return;
    if (cart.currency) activeCurrency = cart.currency;

    // Nothing to switch unless a one-time line has a plan its variant is sold on.
    var hasOneTime = (cart.items || []).some(function (item) {
      return !item.selling_plan_allocation && firstSellingPlanId(item);
    });
    eachIn('[data-subscribe-toggle]', function (btn) {
      btn.hidden = !hasOneTime;
    });

    // Line items — rebuilt per root so each reads its own render settings.
    eachIn('[data-cart-items]', function (el, root) {
      el.innerHTML = (cart.items || []).map(function (item) {
        return lineItemHTML(item, root);
      }).join('');
    });

    // Count
    eachIn(
      '[data-cart-total-items], .oddit-cart-drawer__count, .oddit-cart-drawer__subtotal-count',
      function (el) {
        el.textContent = cart.item_count;
      }
    );

    // Subtotal
    eachIn('[data-cart-subtotal]', function (el) {
      el.textContent = formatMoney(cart.total_price);
    });

    // Progress bar
    updateProgressBar(cart);

    // Savings row
    updateSavings(cart);

    // Toggle empty vs filled states
    var isEmpty = cart.item_count === 0;
    eachIn('[data-cart-empty]', function (el) {
      if (isEmpty) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
    eachIn('[data-cart-filled]', function (el) {
      if (isEmpty) el.setAttribute('hidden', ''); else el.removeAttribute('hidden');
    });
    eachIn('[data-cart-state]', function (el) {
      var state = el.getAttribute('data-cart-state');
      var show = (isEmpty && state === 'empty') || (!isEmpty && state !== 'empty');
      if (show) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });

    // Filter upsells: hide cards for products already in cart.
    // NOTE: never touch [data-cart-additional-checkout] — it hosts Shopify's
    // <shopify-accelerated-checkout-cart> web component and must be preserved.
    var productIds = (cart.items || []).map(function (i) { return String(i.product_id); });
    eachIn('[data-product-id]', function (card) {
      if (productIds.indexOf(String(card.getAttribute('data-product-id'))) !== -1) {
        card.setAttribute('hidden', '');
      } else {
        card.removeAttribute('hidden');
      }
    });

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  function updateProgressBar(cart) {
    if (!threshold || threshold <= 0) return;
    var cartTotal = (cart.total_price || 0) / 100;
    var percentage = Math.min((cartTotal / threshold) * 100, 100);
    var remaining = Math.max(threshold - cartTotal, 0);
    var remainingMoney = formatMoney(Math.round(remaining * 100));

    eachIn(
      '[data-cart-progress-bar], .oddit-cart-drawer__shipping-fill, .oddit-cart-drawer__progress-fill',
      function (el) {
        el.style.width = percentage + '%';
      }
    );

    eachIn('[data-cart-progress-label], .oddit-cart-drawer__shipping-text', function (el) {
      var template = el.getAttribute('data-progress-template') || '';
      if (remaining <= 0) {
        var success = el.getAttribute('data-success-text') || el.getAttribute('data-progress-success') || '';
        if (success) el.innerHTML = success;
      } else if (template) {
        el.innerHTML = template.replace(/\{amount\}/g, remainingMoney);
      }
    });
  }

  // Mirrors the savings block in sections/oddit-cart-drawer.liquid. The row is
  // always present in the DOM, so both the amount and its visibility have to be
  // recomputed here or it goes stale as the cart crosses the threshold.
  function updateSavings(cart) {
    var savings = (cart.items || []).reduce(function (sum, item) {
      var price = priceParts(item);
      return price.compare > price.final ? sum + (price.compare - price.final) : sum;
    }, 0);

    var freeShipping = threshold <= 0 || ((cart.total_price || 0) / 100) >= threshold;

    eachIn('[data-cart-savings-row]', function (row) {
      var parts = [];
      if (savings > 0) parts.push(formatMoney(savings));
      if (freeShipping) {
        parts.push(row.getAttribute('data-free-shipping-text') || 'FREE SHIPPING');
      }

      var valueEl = row.querySelector('[data-cart-savings-value]');
      if (valueEl) valueEl.textContent = parts.join(' + ');
      if (parts.length) row.removeAttribute('hidden');
      else row.setAttribute('hidden', '');
    });
  }

  // -----------------------------------------------------------------
  // Wire cross-sell / upsell / subscription-switch buttons
  // -----------------------------------------------------------------
  function wireButton(btn, handler) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.disabled) return;
      btn.disabled = true;
      Promise.resolve()
        .then(handler)
        .catch(function (err) {
          if (window.console) console.error('[cart-drawer]', err);
        })
        .then(function () {
          btn.disabled = false;
        });
    }, { signal: signal });
  }

  function bindUpsells() {
    // Cross-sell add buttons
    eachIn('[data-add-to-cart][data-variant-id], .oddit-cart-drawer__rec-add[data-variant-id], .oddit-cart-drawer__product-add[data-variant-id]', function (btn) {
      if (btn.dataset.cartBound) return;
      btn.dataset.cartBound = '1';
      wireButton(btn, function () {
        return addToCart(Number(btn.getAttribute('data-variant-id')));
      });
    });

    // Bundle upsell CTA (multi-variant add)
    eachIn('.oddit-cart-drawer__upsell-cta[data-variant-id]', function (btn) {
      if (btn.dataset.cartBound) return;
      btn.dataset.cartBound = '1';
      wireButton(btn, function () {
        var raw = btn.getAttribute('data-variant-id') || '';
        var ids = raw.split(',').map(function (s) { return Number(s.trim()); }).filter(Boolean);
        if (ids.length > 1) {
          return addMultipleToCart(ids.map(function (id) { return { id: id, quantity: 1 }; }));
        }
        return addToCart(ids[0] || Number(raw));
      });
    });

    // Cart-wide subscription switch CTA. Switches every one-time line to its OWN
    // first selling plan — a plan id belongs to one variant, so a single id from
    // the button could only ever be right for a single-product cart.
    // Per-line switches ([data-line-switch]) are handled by delegation instead;
    // binding them here too fired both handlers on one click.
    eachIn('[data-subscribe-toggle]:not([data-line-switch])', function (btn) {
      if (btn.dataset.cartBound) return;
      btn.dataset.cartBound = '1';
      wireButton(btn, function () {
        var overrideId = Number(btn.getAttribute('data-selling-plan-id')) || null;
        var targetKey = btn.getAttribute('data-line-item-key');
        return getCart().then(ensureProducts).then(function (cart) {
          var targets = (cart.items || []).filter(function (i) {
            if (i.selling_plan_allocation) return false;
            return targetKey ? i.key === targetKey : true;
          });
          var chain = Promise.resolve();
          targets.forEach(function (item) {
            var planId = firstSellingPlanId(item) || overrideId;
            if (!planId) return; // Variant has no subscription option — leave it.
            chain = chain.then(function () {
              return updateLineItemToSubscription(item.key, planId, item.quantity);
            });
          });
          return chain;
        });
      });
    });
  }

  // -----------------------------------------------------------------
  // Delegated handlers for line-item quantity + remove
  // -----------------------------------------------------------------
  // Delegated on the document so every root is covered — including the /cart page,
  // whose items container is a sibling of the drawer's, not inside it.
  document.addEventListener('click', function (e) {
    var itemsContainer = e.target.closest && e.target.closest('.oddit-cart-drawer [data-cart-items]');
    if (!itemsContainer) return;
    // Per-line subscription switch — delegated so it survives re-renders.
    var switchBtn = e.target.closest('[data-line-switch]');
    if (switchBtn) {
      e.preventDefault();
      if (switchBtn.disabled) return;
      var sKey = switchBtn.getAttribute('data-line-item-key');
      if (!sKey) return;
      switchBtn.disabled = true;
      Promise.resolve()
        .then(function () {
          // Always resolve against the live cart: the plan has to be one the
          // variant is sold on, and the switch has to carry the line quantity.
          return getCart().then(ensureProducts).then(function (cart) {
            var line = (cart.items || []).filter(function (i) { return i.key === sKey; })[0];
            if (!line) return;
            var planId = firstSellingPlanId(line)
              || Number(switchBtn.getAttribute('data-selling-plan-id'))
              || null;
            if (planId) return updateLineItemToSubscription(sKey, planId, line.quantity);
          });
        })
        .catch(function (err) {
          if (window.console) console.error('[cart-drawer]', err);
        })
        .then(function () {
          switchBtn.disabled = false;
        });
      return;
    }

    var removeBtn = e.target.closest('[data-remove-line], [data-remove-item]');
    if (removeBtn) {
      e.preventDefault();
      var rKey = removeBtn.getAttribute('data-line-key') || removeBtn.getAttribute('data-line-item-key');
      if (!rKey || removeBtn.disabled) return;
      removeBtn.disabled = true;
      removeFromCart(rKey).catch(function (err) {
        if (window.console) console.error('[cart-drawer]', err);
      }).then(function () {
        removeBtn.disabled = false;
      });
      return;
    }

    var qBtn = e.target.closest('[data-quantity-action], [data-quantity-decrease], [data-quantity-increase]');
    if (!qBtn) return;
    e.preventDefault();
    if (qBtn.disabled) return;

    var key = qBtn.getAttribute('data-line-key') || qBtn.getAttribute('data-line-item-key');
    if (!key) return;

    var action = qBtn.getAttribute('data-quantity-action');
    if (!action) action = qBtn.hasAttribute('data-quantity-increase') ? 'increase' : 'decrease';

    var valueEl = itemsContainer.querySelector('[data-quantity-value][data-line-item-key="' + key + '"]');
    var current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
    if (isNaN(current)) current = 1;
    var next = action === 'increase' ? current + 1 : current - 1;
    if (next < 0) next = 0;

    qBtn.disabled = true;
    updateLineItemQuantity(key, next).catch(function (err) {
      if (window.console) console.error('[cart-drawer]', err);
    }).then(function () {
      qBtn.disabled = false;
    });
  }, { signal: signal });

  // -----------------------------------------------------------------
  // Checkout
  // -----------------------------------------------------------------
  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', function (e) {
      // Anchor tag would navigate anyway; button needs the redirect.
      if (checkoutBtn.tagName === 'A' && checkoutBtn.getAttribute('href')) return;
      e.preventDefault();
      window.location.href = '/checkout';
    }, { signal: signal });
  }

  // -----------------------------------------------------------------
  // Drag-to-scroll recommendations rail (kept from static UI)
  // -----------------------------------------------------------------
  if (products) {
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;

    products.addEventListener('dragstart', function (e) { e.preventDefault(); }, { signal: signal });

    var pointerId = null;

    products.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      dragging = true;
      moved = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = products.scrollLeft;
      // NO setPointerCapture here. Capturing on a bare pointerdown retargets the
      // click to this rail, so buttons inside it (the cross-sell adds) never
      // receive their own click. Capture only once a real drag starts — same as
      // oddit-section-testimonials.js / oddit-section-ugc.js.
    }, { signal: signal });

    products.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;

      if (!moved) {
        if (Math.abs(dx) < 5) return;
        moved = true;
        products.classList.add('is-dragging');
        try { products.setPointerCapture(pointerId); } catch (err) {}
      }

      e.preventDefault();
      products.scrollLeft = startScroll - dx;
    }, { signal: signal });

    ['pointerup', 'pointercancel'].forEach(function (type) {
      products.addEventListener(type, function (e) {
        if (!dragging || (e && e.pointerId !== pointerId)) return;
        try { products.releasePointerCapture(pointerId); } catch (err) {}
        dragging = false;
        pointerId = null;
        products.classList.remove('is-dragging');
      }, { signal: signal });
    });

    // Swallow the click that ends a drag, then clear the flag so the NEXT click
    // (a real one) still gets through.
    products.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, { capture: true, signal: signal });
  }

  // -----------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------
  function init() {
    bindUpsells();
    // Sync with server so header count etc. reflect canonical state
    refreshCart().catch(function () {});
  }

  // Theme editor / `shopify theme dev` lifecycle.
  //
  // These two MUST NOT use `signal`: unload aborts it, and addEventListener
  // with an aborted signal is a silent no-op — binding them to it would remove
  // the very listener that revives the drawer, leaving it dead until a full
  // page load. A section reload also swaps in fresh DOM, so re-resolve the
  // cached nodes; the old ones are detached.
  document.addEventListener('shopify:section:load', function (e) {
    var fresh = e.target && e.target.querySelector && e.target.querySelector('.oddit-cart-drawer');
    if (!fresh) return;
    resolveRoots();
    if (!drawer) return;
    resolveRefs();
    if (signal.aborted) {
      controller = new AbortController();
      signal = controller.signal;
    }
    init();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.contains(drawer)) controller.abort();
  });

  // Add buttons outside the drawer (sticky ATC, PDP cross-sells) have no
  // section JS of their own. bindUpsells only reaches into the drawer, so wire
  // them here by delegation — they also survive any innerHTML re-render.
  // Opt out with data-cart-managed when a section handles its own click
  // (e.g. the PDP main ATC, which carries a selling plan).
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-add-to-cart][data-variant-id]');
    if (!btn || btn.disabled) return;
    if (btn.closest('.oddit-cart-drawer')) return;
    if (btn.hasAttribute('data-cart-managed')) return;
    btn.disabled = true;
    Promise.resolve(addToCart(Number(btn.getAttribute('data-variant-id'))))
      .catch(function (err) {
        if (window.console) console.error('[cart-drawer]', err);
      })
      .then(function () {
        btn.disabled = false;
      });
  }, { signal: signal });

  // Public API
  window.OdditCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: function () {
      if (drawer.classList.contains('is-open')) closeDrawer(); else openDrawer();
    },
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart
  };

  init();
})();